import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { SessionUser, ShopSummary } from '@outfalak/shared'
import { api, ApiError } from '../lib/api.js'
import { isEmbedded } from '../lib/shopify.js'
import { realtime } from '../lib/realtime.js'

interface Session {
  user: SessionUser | null
  shop: ShopSummary | null
  shops: ShopSummary[]
}

interface AuthValue extends Session {
  /** Null while the first /me call is in flight — not the same as signed out. */
  loading: boolean
  /** True when this is rendering inside the Shopify admin's iframe. */
  embedded: boolean
  /**
   * Why the embedded admin could not connect, if it could not.
   *
   * Kept apart from an ordinary signed-out state because the two need opposite
   * screens: a merchant on app.outfalakcheckout.com should see a sign-in form, and
   * one inside Shopify must never — they have no password with us, so a login
   * box in that iframe is a dead end.
   */
  embeddedError: string | null
  /** Resolves to a challenge when the account has two-step verification on. */
  signIn: (email: string, password: string) => Promise<TwoFactorChallenge | null>
  /**
   * Sign in with a verified phone number.
   *
   * `mode` is what separates the two doors. Signing *in* with a number that has
   * no account is refused rather than quietly creating one — the failure that
   * makes a mistyped digit look like a dashboard that lost every store.
   */
  signInWithOtp: (phone: string, code: string, mode: 'signin' | 'signup') => Promise<TwoFactorChallenge | null>
  /** Answers a challenge; signed in when it resolves. */
  verifyTwoFactor: (token: string, code: string) => Promise<void>
  signOut: () => Promise<void>
  /** Revokes every session on every device, this one included. */
  signOutEverywhere: () => Promise<void>
  switchShop: (shopId: string) => Promise<void>
  /** Re-reads /me — after a store is created here, so the switcher and current store pick it up. */
  refresh: () => Promise<void>
}

/** A sign-in that passed its first factor and now needs a code. */
export interface TwoFactorChallenge {
  token: string
  method: 'totp' | 'whatsapp'
  phoneHint: string | null
}

const AuthContext = createContext<AuthValue | null>(null)

const EMPTY: Session = { user: null, shop: null, shops: [] }

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [session, setSession] = useState<Session>(EMPTY)
  const [loading, setLoading] = useState(true)
  const [embeddedError, setEmbeddedError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      /*
       * Inside the Shopify admin, this is the install.
       *
       * Under managed installation there is no callback to hang it off —
       * Shopify collects the merchant's approval itself and simply opens the
       * app — so "have we got a token for this store?" is a question asked on
       * every open, not once. The endpoint is idempotent and answers from the
       * database when the store is already connected, so the cost of asking
       * every time is one round trip.
       *
       * It runs before /me on purpose: /me resolves the caller by looking the
       * store up, and on a first-ever open there is no store yet to find.
       */
      if (isEmbedded()) {
        try {
          await api('/v1/shopify/connect', { method: 'POST' })
          setEmbeddedError(null)
        } catch (error) {
          setEmbeddedError(
            error instanceof ApiError
              ? error.message
              : 'Could not connect this store to Outfalak Checkout.',
          )
        }
      }

      setSession(await api<Session>('/v1/auth/me'))
    } catch (error) {
      // 401 here is the ordinary "not signed in" case on first load, not a
      // failure worth surfacing. Anything else is left to the screen to report.
      if (!(error instanceof ApiError && error.status === 401)) console.error(error)
      setSession(EMPTY)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  // The socket follows the session: opened once there is one, closed the moment
  // there is not. Connecting before /me returns would authenticate against a
  // cookie that may not exist and burn a reconnect cycle on a 4401.
  useEffect(() => {
    if (session.user || session.shop) {
      realtime.connect()
      return () => realtime.disconnect()
    }
    return undefined
  }, [session.user, session.shop])

  const value = useMemo<AuthValue>(
    () => ({
      ...session,
      loading,
      embedded: isEmbedded(),
      embeddedError,
      async signIn(email, password) {
        const result = await api<{ user?: SessionUser; twoFactor?: TwoFactorChallenge }>('/v1/auth/login', {
          method: 'POST',
          body: { email, password },
        })
        if (result.twoFactor) return result.twoFactor
        // Re-reading rather than trusting the login response: it returns the
        // user but not the store list, and a half-populated session is how a
        // store picker ends up empty until a refresh.
        setSession({ ...EMPTY, user: result.user ?? null })
        await load()
        return null
      },
      async signInWithOtp(phone, code, mode) {
        const result = await api<{ user?: SessionUser; twoFactor?: TwoFactorChallenge }>('/v1/auth/otp/verify', {
          method: 'POST',
          body: { phone, code, mode },
        })
        if (result.twoFactor) return result.twoFactor
        // Re-read for the same reason the password path does: verify returns the
        // user but not the store list, and a half-populated session is how a
        // store picker ends up empty until a refresh.
        setSession({ ...EMPTY, user: result.user ?? null })
        await load()
        return null
      },
      async verifyTwoFactor(token, code) {
        await api('/v1/auth/2fa/verify', { method: 'POST', body: { token, code } })
        await load()
      },
      async signOut() {
        await api('/v1/auth/logout', { method: 'POST' })
        setSession(EMPTY)
      },
      async signOutEverywhere() {
        await api('/v1/auth/logout-everywhere', { method: 'POST' })
        setSession(EMPTY)
      },
      async switchShop(shopId) {
        const { shop } = await api<{ shop: ShopSummary }>('/v1/auth/switch-shop', {
          method: 'POST',
          body: { shopId },
        })
        setSession((current) => ({ ...current, shop }))
        // The subscription token names the channels for the store that was
        // current when it was minted. Switching means a new one.
        realtime.disconnect()
        realtime.connect()
      },
      refresh: load,
    }),
    [session, loading, embeddedError, load],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>')
  return value
}
